import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Lock, Check, Minus, Eye, Pencil } from "lucide-react";
import { invalidateClientPerms } from "../lib/useClientPerms";
import { useRoles } from "../lib/useRoles";
import { RoleManager } from "./RoleManager";

export const STAFF_AREA_LABELS = {
  company: "Company Profile", employees: "Employees", time: "Time", people: "People", run_payroll: "Run Payroll",
  history: "Pay History", remittance: "Remittance", reports: "Reports", t4: "T4 / ROE", migration: "Migration", notifications: "Notifications",
  companies_manage: "Create client companies",
  settings_transmitter: "Settings › CRA Transmitter (T619)", settings_branding: "Settings › Branding & Time zone",
  settings_notifications: "Settings › Notifications", data_import: "Settings › Data Import", settings_payroll_history: "Settings › Payroll History import",
  codes: "Settings › Earning & Deduction Codes",
};
// Visual grouping of the matrix rows (areas not listed fall into the first group).
export const STAFF_AREA_GROUPS = [
  ["Client workspace", ["company", "employees", "time", "people", "run_payroll", "history", "remittance", "reports", "t4", "migration", "notifications"]],
  ["Firm", ["companies_manage"]],
  ["Settings tabs", ["settings_transmitter", "settings_branding", "settings_notifications", "data_import", "settings_payroll_history", "codes"]],
];
export const groupAreas = (areas, groups) => groups.map(([label, ids]) => [label, ids.filter((a) => areas.includes(a))]).filter(([, ids]) => ids.length);
export const STAFF_ROLE_LABELS = { master: "Admin (full access)", officer: "Payroll Officer", readonly: "Read-only" };

let staffCache = null;
export const fetchStaffPerms = () => staffCache ? Promise.resolve(staffCache)
  : api.get("/settings/staff-permissions").then((r) => { staffCache = r.data; return r.data; });
export const invalidateStaffPerms = () => { staffCache = null; invalidateClientPerms(); };

// Effective per-area permissions for one teammate: role matrix + per-teammate overrides (master only may edit).
export function TeammatePermissions({ staffRole, userId, overrides: initial, onSaved }) {
  const roles = useRoles();
  const [data, setData] = useState(null);
  const [ov, setOv] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { fetchStaffPerms().then(setData).catch(() => setData({ areas: [], permissions: {}, master_only: [] })); }, []);
  useEffect(() => { setOv(initial || {}); }, [initial, userId]);
  if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
  const role = data.permissions?.[staffRole] || {};
  const isMaster = staffRole === "master";
  const eff = (a, k) => {
    const o = expired(a) ? {} : (ov[a] || {});
    if (o[k] === true || o[k] === false) return k === "edit" ? (o.edit && o.view !== false) : o[k];
    return !!role[a]?.[k];
  };
  const setOverride = (a, k, val) => setOv((p) => {
    const row = { ...(p[a] || {}) };
    if (k === "expires_on") { if (val) row.expires_on = val; else delete row.expires_on; }
    else if (val === "role") delete row[k]; else row[k] = val === "allow";
    if (row.edit === true) row.view = true;
    if (row.view === false) row.edit = false;
    const next = { ...p };
    if (row.view === undefined && row.edit === undefined) delete next[a]; else next[a] = row;
    return next;
  });
  const expired = (a) => ov[a]?.expires_on && ov[a].expires_on < new Date().toISOString().slice(0, 10);
  const dirty = JSON.stringify(ov) !== JSON.stringify(initial || {});
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/staff/${userId}/permissions`, { overrides: ov }); invalidateStaffPerms(); toast.success("Teammate permissions saved"); onSaved && onSaved(r.data.perm_overrides); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const Sel = ({ a, k }) => {
    const o = ov[a] || {}; const v = o[k] === true ? "allow" : o[k] === false ? "deny" : "role";
    return (
      <select data-testid={`ov-${userId}-${a}-${k}`} value={v} onChange={(e) => setOverride(a, k, e.target.value)} className={`border border-input bg-white px-1.5 py-0.5 text-[11px] ${v !== "role" ? "border-amber-500" : ""}`}>
        <option value="role">Role default</option><option value="allow">Allow</option><option value="deny">Deny</option>
      </select>
    );
  };
  return (
    <div data-testid={`teammate-perms-${staffRole}`}>
      <div className={`grid ${userId && !isMaster ? "grid-cols-1" : "sm:grid-cols-2"} gap-x-8 gap-y-1 text-xs`}>
        {data.areas.map((a) => (
          <div key={a} className="flex items-center justify-between gap-2 border-b border-border/60 py-1">
            <span className="truncate">{STAFF_AREA_LABELS[a] || a}</span>
            <span className="flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] shrink-0">
              <span className={`inline-flex items-center gap-1 w-14 ${eff(a, "view") ? "text-emerald-700" : "text-muted-foreground"}`}><Eye className="h-3 w-3" />{eff(a, "view") ? "view" : "—"}</span>
              {!isMaster && userId && <Sel a={a} k="view" />}
              <span className={`inline-flex items-center gap-1 w-14 ${eff(a, "edit") ? "text-emerald-700" : "text-muted-foreground"}`}><Pencil className="h-3 w-3" />{eff(a, "edit") ? "edit" : "—"}</span>
              {!isMaster && userId && <Sel a={a} k="edit" />}
              {!isMaster && userId && ov[a] && (
                <input data-testid={`ov-${userId}-${a}-expires`} type="date" value={ov[a].expires_on || ""} onChange={(e) => setOverride(a, "expires_on", e.target.value)} title="Override expires on (optional)" className={`border border-input bg-white px-1 py-0.5 text-[10px] ${expired(a) ? "border-red-500 text-red-600" : ""}`} />
              )}
            </span>
          </div>
        ))}
      </div>
      {!userId && <div className="mt-2 text-[11px] text-muted-foreground">
        <span className="font-medium text-foreground">Admin-only actions:</span> {(data.master_only || []).join(" · ")} — {isMaster ? "allowed" : "not allowed for this role"}.
      </div>}
      {!isMaster && userId && (
        <div className="mt-3 flex items-center gap-3">
          <button data-testid={`ov-save-${userId}`} disabled={busy || !dirty} onClick={save} className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-1.5 text-xs hover:bg-neutral-800 disabled:opacity-40"><Save className="h-3.5 w-3.5" /> Save overrides for this teammate</button>
          {Object.keys(ov).length > 0 && <span className="text-[11px] text-amber-700">{Object.keys(ov).length} area override{Object.keys(ov).length === 1 ? "" : "s"} (amber) on top of the {roles.label("staff", staffRole)} role</span>}
        </div>
      )}
    </div>
  );
}

export function StaffPermsPanel({ embedded = false }) {
  const roles = useRoles();
  const EDITABLE_ROLES = roles.staff.filter((r) => r.key !== "master").map((r) => [r.key, r.label]);
  const [data, setData] = useState(null);
  const [perms, setPerms] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = !!perms && JSON.stringify(perms) !== JSON.stringify(saved);

  const apply = (d) => { setData(d); setPerms(d.permissions); setSaved(d.permissions); };
  const load = () => { staffCache = null; api.get("/settings/staff-permissions").then((r) => apply(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const toggle = (role, area, key) => setPerms((p) => {
    const cur = { ...p[role][area], [key]: !p[role][area][key] };
    if (key === "view" && !cur.view) cur.edit = false;
    if (key === "edit" && cur.edit) cur.view = true;
    return { ...p, [role]: { ...p[role], [area]: cur } };
  });

  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/settings/staff-permissions", { permissions: perms }); invalidateStaffPerms(); apply(r.data); toast.success("Staff permissions saved"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset staff permissions to the standard defaults?")) return;
    setBusy(true);
    try { const r = await api.post("/settings/staff-permissions/reset"); invalidateStaffPerms(); apply(r.data); toast.success("Staff permissions reset to defaults"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!perms) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div data-testid="staff-perms-panel" className={embedded ? "" : "border border-border bg-white p-6 mb-6"}>
      {!embedded && <>
        <h2 className="font-bold tracking-tight">Staff roles & permissions</h2>
        <p className="text-xs text-muted-foreground mt-1">What each Postings Inc staff role can view and edit across every client company. Admin (full access) is always unrestricted.</p>
      </>}
      <div className={embedded ? "mt-2" : "mt-5"}><RoleManager kind="staff" onChanged={load} /></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-border">
          <thead>
            <tr className="bg-neutral-50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <th className="text-left px-4 py-2 border-b border-border">Area</th>
              <th className="px-4 py-2 border-b border-l border-border text-center" colSpan={2}>Admin (full access)</th>
              {EDITABLE_ROLES.map(([rk, rl]) => <th key={rk} className="px-4 py-2 border-b border-l border-border text-center" colSpan={2}>{rl}</th>)}
            </tr>
            <tr className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-1.5 border-b border-border"></th>
              <th className="px-2 py-1.5 border-b border-l border-border text-center">View</th><th className="px-2 py-1.5 border-b border-border text-center">Edit</th>
              {EDITABLE_ROLES.map(([rk]) => (<><th key={rk + "v"} className="px-2 py-1.5 border-b border-l border-border text-center">View</th><th key={rk + "e"} className="px-2 py-1.5 border-b border-border text-center">Edit</th></>))}
            </tr>
          </thead>
          <tbody>
            {groupAreas(data.areas, STAFF_AREA_GROUPS).map(([group, ids]) => [
              <tr key={"g-" + group} data-testid={`sperm-group-${group.replace(/\W+/g, "-").toLowerCase()}`} className="bg-neutral-100/80"><td colSpan={3 + EDITABLE_ROLES.length * 2} className="px-4 py-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{group}</td></tr>,
              ...ids.map((a) => (
              <tr key={a} className="border-b border-border last:border-0">
                <td className="px-4 py-2.5 font-medium">{STAFF_AREA_LABELS[a] || a}</td>
                <td className="px-2 py-2.5 border-l border-border text-center text-emerald-700"><Check className="h-4 w-4 inline" /></td>
                <td className="px-2 py-2.5 text-center text-emerald-700"><Check className="h-4 w-4 inline" /></td>
                {EDITABLE_ROLES.map(([rk]) => (
                  <>
                    <td key={rk + "v"} className="px-2 py-2.5 border-l border-border text-center">
                      <input type="checkbox" data-testid={`sperm-${rk}-${a}-view`} checked={!!perms[rk]?.[a]?.view} onChange={() => toggle(rk, a, "view")} className="h-4 w-4 accent-black" />
                    </td>
                    <td key={rk + "e"} className="px-2 py-2.5 text-center">
                      <input type="checkbox" data-testid={`sperm-${rk}-${a}-edit`} checked={!!perms[rk]?.[a]?.edit} onChange={() => toggle(rk, a, "edit")} className="h-4 w-4 accent-black" />
                    </td>
                  </>
                ))}
              </tr>
              ))])}
            {(data.master_only || []).map((label) => (
              <tr key={label} className="border-b border-border last:border-0 bg-neutral-50/60">
                <td className="px-4 py-2 text-muted-foreground flex items-center gap-2"><Lock className="h-3 w-3" /> {label}</td>
                <td className="px-2 py-2 border-l border-border text-center text-emerald-700"><Check className="h-4 w-4 inline" /></td>
                <td className="px-2 py-2 text-center text-emerald-700"><Check className="h-4 w-4 inline" /></td>
                {EDITABLE_ROLES.map(([rk]) => (<><td key={rk + "v"} className="px-2 py-2 border-l border-border text-center text-muted-foreground"><Minus className="h-4 w-4 inline" /></td><td key={rk + "e"} className="px-2 py-2 text-center text-muted-foreground"><Minus className="h-4 w-4 inline" /></td></>))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button data-testid="save-staff-perms" disabled={busy || !dirty} onClick={save} className="inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save staff permissions"}</button>
        <button data-testid="reset-staff-perms" disabled={busy} onClick={reset} className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 text-sm font-medium hover:border-black transition-colors disabled:opacity-50"><RotateCcw className="h-4 w-4" /> Reset to defaults</button>
        {dirty && <span data-testid="staff-perms-unsaved" className="text-xs text-amber-700">Unsaved changes</span>}
      </div>
    </div>
  );
}

// Assign client companies to a teammate. Empty = all clients. Admin accounts always see everything.
export function TeammateClients({ userId, staffRole, assigned, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [sel, setSel] = useState(assigned || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/companies").then((r) => setCompanies([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" })))).catch(() => {}); }, []);
  useEffect(() => { setSel(assigned || []); }, [assigned, userId]);
  const dirty = JSON.stringify([...sel].sort()) !== JSON.stringify([...(assigned || [])].sort());
  const toggle = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/staff/${userId}/clients`, { company_ids: sel }); toast.success(sel.length ? `Assigned ${sel.length} client${sel.length === 1 ? "" : "s"}` : "Teammate now sees all clients"); onSaved && onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (staffRole === "master") return <p data-testid={`clients-all-${userId}`} className="text-xs text-muted-foreground">Admin (full access) — sees every client company.</p>;
  return (
    <div data-testid={`teammate-clients-${userId}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assigned clients</span>
        <span className="text-[11px] text-muted-foreground">{sel.length === 0 ? "None selected = access to ALL clients" : `${sel.length} of ${companies.length} — only these clients are visible to this teammate`}</span>
        <div className="ml-auto flex gap-2">
          <button data-testid={`clients-select-all-${userId}`} onClick={() => setSel(companies.map((c) => c.company_id))} className="text-[11px] border border-border px-2 py-0.5 hover:border-black">Select all</button>
          <button data-testid={`clients-clear-${userId}`} onClick={() => setSel([])} className="text-[11px] border border-border px-2 py-0.5 hover:border-black">Clear (all clients)</button>
        </div>
      </div>
      <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 max-h-48 overflow-y-auto pr-1">
        {companies.map((c) => (
          <label key={c.company_id} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
            <input data-testid={`client-assign-${userId}-${c.company_id}`} type="checkbox" checked={sel.includes(c.company_id)} onChange={() => toggle(c.company_id)} className="h-3.5 w-3.5 accent-black" />
            <span className="truncate">{c.name}</span>
          </label>
        ))}
      </div>
      <button data-testid={`clients-save-${userId}`} disabled={busy || !dirty} onClick={save} className="mt-3 inline-flex items-center gap-1.5 bg-black text-white px-3 py-1.5 text-xs hover:bg-neutral-800 disabled:opacity-40"><Save className="h-3.5 w-3.5" /> Save client assignments</button>
    </div>
  );
}
